import { Plans } from 'common/modules/plans';
import { RentleProductRef, RentleProduct } from './types';

/**
 * Rentle plan products
 */

export const NewPlanProducts = {
	[RentleProductRef.BASIC_PLAN]: {
		ref: RentleProductRef.BASIC_PLAN,
		description: 'Rentle Basic subscription',
		planRef: Plans.BASIC,
	},
	[RentleProductRef.GROW_PLAN]: {
		ref: RentleProductRef.GROW_PLAN,
		description: 'Rentle Grow subscription',
		planRef: Plans.GROW,
	},
	[RentleProductRef.ADVANCED_PLAN]: {
		ref: RentleProductRef.ADVANCED_PLAN,
		description: 'Rentle Advanced subscription',
		planRef: Plans.ADVANCED,
	},
};

/**
 * Rentle plan product discounts
 */

export const RevenueDiscountProducts = {
	[RentleProductRef.BASIC_REVENUE_DISCOUNT]: {
		ref: RentleProductRef.BASIC_REVENUE_DISCOUNT,
		description: 'Rentle Basic revenue discount (-50%)',
		planRef: Plans.BASIC,
	},
	[RentleProductRef.GROW_REVENUE_DISCOUNT]: {
		ref: RentleProductRef.GROW_REVENUE_DISCOUNT,
		description: 'Rentle Grow revenue discount (-50%)',
		planRef: Plans.GROW,
	},
	[RentleProductRef.ADVANCED_REVENUE_DISCOUNT]: {
		ref: RentleProductRef.ADVANCED_REVENUE_DISCOUNT,
		description: 'Rentle Advanced revenue discount (-50%)',
		planRef: Plans.ADVANCED,
	},
};

export const SubscriptionDiscountProducts = {
	[RentleProductRef.SUBSCRIPTION_START_DISCOUNT]: {
		ref: RentleProductRef.SUBSCRIPTION_START_DISCOUNT,
		description: 'Subscription start discount',
	},
	[RentleProductRef.SUBSCRIPTION_CANCEL_REFUND]: {
		ref: RentleProductRef.SUBSCRIPTION_CANCEL_REFUND,
		description: 'Subscription cancel refund',
	},
};

/**
 * Merchant solution products
 */

export const MerchantSolutionProducts = {
	[RentleProductRef.DELIVERY_MERCHANT_SERVICE_BOOKING]: {
		ref: RentleProductRef.DELIVERY_MERCHANT_SERVICE_BOOKING,
		description: 'Delivery, booking fee',
	},
	[RentleProductRef.API_WEBHOOKS_USAGE]: {
		ref: RentleProductRef.API_WEBHOOKS_USAGE,
		description: 'API & Webhooks usage',
	},
};

/**
 * Rentle legacy plans
 */

export const OldPlanProducts = {
	[RentleProductRef.CORE_COMMISSION_BOOKING]: {
		ref: RentleProductRef.CORE_COMMISSION_BOOKING,
		description: 'Core commission, booking fee',
	},
	[RentleProductRef.CORE_FIXED_MONTHLY]: {
		ref: RentleProductRef.CORE_FIXED_MONTHLY,
		description: 'Core fixed, monthly subscription fee',
	},
	[RentleProductRef.CORE_FIXED_ANNUAL]: {
		ref: RentleProductRef.CORE_FIXED_ANNUAL,
		description: 'Core fixed, annual subscription fee',
	},
};

/**
 * Custom products that can be assigned to subscription invoices
 */

export const CustomProducts = {
	[RentleProductRef.CUSTOM_DISCOUNT_ONE_OFF]: {
		ref: RentleProductRef.CUSTOM_DISCOUNT_ONE_OFF,
		description: 'Custom discount, one-off',
	},
	[RentleProductRef.CUSTOM_FEE_ONE_OFF]: {
		ref: RentleProductRef.CUSTOM_FEE_ONE_OFF,
		description: 'Custom fee, one-off',
	},
};

/**
 * Credit products
 */

export const CreditsProducts = {
	[RentleProductRef.CREDITS_USED]: {
		ref: RentleProductRef.CREDITS_USED,
		description: 'Credits used',
	},
};

export const RentleProducts: Record<RentleProductRef, RentleProduct> = {
	...OldPlanProducts,
	...MerchantSolutionProducts,
	...NewPlanProducts,
	...RevenueDiscountProducts,
	...SubscriptionDiscountProducts,
	...CustomProducts,
	...CreditsProducts,
};

export const isProductRef = <T extends RentleProductRef>(refs: T[]) => (
	ref: RentleProductRef,
): ref is typeof refs[number] => !!refs.find((r) => r === ref);

const getKeys = (obj: Partial<Record<RentleProductRef, RentleProduct>>) =>
	Object.keys(obj) as Array<keyof typeof obj>;

export const isCustomProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(CustomProducts))(ref);

export const isNewPlanProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(NewPlanProducts))(ref);

export const isMerchantSolutionProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(MerchantSolutionProducts))(ref);

export const isCreditsProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(CreditsProducts))(ref);

export const isRevenueDiscountProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(RevenueDiscountProducts))(ref);

export const isSubscriptionDiscountProductRef = (ref: RentleProductRef) =>
	isProductRef(getKeys(SubscriptionDiscountProducts))(ref);

export const isDiscountRentleProductRef = (ref: RentleProductRef) =>
	isRevenueDiscountProductRef(ref) ||
	isSubscriptionDiscountProductRef(ref) ||
	ref === RentleProductRef.CUSTOM_DISCOUNT_ONE_OFF;
